import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getChannelById } from '../../models/channels/selectors';
import ViewTrigger from '../../components/view-trigger';

import ContentLayoutGallery from '../../components/v2/content-layouts/content-layout-gallery';
import ContentLayoutRow from '../../components/v2/content-layouts/content-layout-row';
import ContentLayoutGrid from '../../components/v2/content-layouts/content-layout-grid';
import ContentLayoutHero from '../../components/v2/content-layouts/content-layout-hero';
import { DEFAULT_SKIP_TO_CONTENT_ID } from '../../components/skip-to-content/skip-to-content';
import { useContentFeedQuery } from '../../common/queries/use-content-feed-query';
import {
  InitiativeTagResponse,
  useInitiativeTagsQuery,
} from '../../common/queries/use-initiative-tags-query';
import { Content } from '../../models/content/types';
import { Channel } from '../../models/channels/types';
import { RootPatronState } from '../../common/use-patron-selector';
import { ContentHomepageModule } from '../../models/home-page-custom/types';

/**
 * Props for the HomeFeedSection component
 */
type HomeFeedSectionProps = {
  order: number;
  item: ContentHomepageModule;
  updateSectionContents: (
    order: number,
    newContents: Content[],
    newMaxContents: number
  ) => void;
  contents: Content[];
  initiativeTags: InitiativeTagResponse[];
};

/**
 * Max display contents configuration
 */
type MaxDisplayContents = {
  hero: number;
  grid: number;
  gallery: number;
  row: number;
};

/**
 * HomeFeedSection component that renders a single feed section
 */
const HomeFeedSection = ({
  order,
  item,
  updateSectionContents,
  contents,
  initiativeTags,
}: HomeFeedSectionProps): JSX.Element | null => {
  const { t } = useTranslation();

  // for now, we only support single reference ID, even though the homepage module returns an array.
  // for now, we do not support the 'only_include_followed_topics' option, nor the 'content_inclusion' option.
  // both of these limitations are due to missing API functionaliy in the feed endpoint.

  const {
    source,
    reference_ids,
    display_type: displayType,
    minimum_content_threshold: minContents,
  } = item.attributes.settings;

  const manualFetch = order > 1;
  const channel = useSelector((state: RootPatronState) =>
    getChannelById(state, reference_ids?.[0]?.toString() || '')
  ) as Channel | undefined;

  // Find matching initiative tag by converting filterId to string for comparison
  const initiative = initiativeTags.find(
    (tag) => tag.id === String(reference_ids?.[0])
  )?.attributes;

  const filter = (): string | undefined => {
    if (source === 'latest') return 'newest';
    if (source === 'topic') return undefined;
    return source;
  };

  const displayTitle = item.attributes.name;
  const displayDescription = item.attributes.settings.description;

  const getSectionId = (): string => {
    if (source === 'topic' && channel) return `channel-${channel.id}`;
    if (source === 'initiative' && initiative) {
      return `initiative-${initiative.id}`;
    }
    return source;
  };

  const viewAllText = t('landing.see_all');

  const maxDisplayContents = useMemo<MaxDisplayContents>(
    () => ({
      hero: 4,
      grid: 6,
      gallery: 9,
      row: 3,
    }),
    []
  );

  const viewAllUrl = (): string => {
    if (source === 'topic') return `/channels/${reference_ids?.[0]}`;
    if (source === 'initiative') return `/initiatives/${reference_ids?.[0]}`;
    return `/feed/${source}`;
  };

  const perPage =
    Math.max(...Object.values(maxDisplayContents)) * order +
    maxDisplayContents[displayType];

  // Create a ref to track if we've processed this data already
  const processedDataRef = useRef(false);

  // Use React Query to fetch content
  const {
    data: feedItems = [],
    isLoading,
    isSuccess: loaded,
    refetch: fetchFeed,
  } = useContentFeedQuery({
    filter: filter(),
    perPage: perPage,
    channelId: source === 'topic' ? reference_ids?.[0]?.toString() : undefined,
    initiativeId:
      source === 'initiative' ? reference_ids?.[0]?.toString() : undefined,
    includeTypes: displayType === 'gallery' ? ['image'] : undefined,
  });

  // Use effect to handle content updates with ref to prevent infinite loops
  useEffect(() => {
    if (loaded && feedItems.length > 0 && !processedDataRef.current) {
      updateSectionContents(order, feedItems, maxDisplayContents[displayType]);
      processedDataRef.current = true;
    }
  }, [
    loaded,
    feedItems,
    order,
    maxDisplayContents,
    displayType,
    updateSectionContents,
  ]);

  // Reset the ref when key dependencies change that would require a new data update
  useEffect(() => {
    processedDataRef.current = false;
  }, [order, displayType, source, reference_ids]);

  const shouldDisplay = (): boolean => {
    // The home feed component assumes an array of content with length > 1
    // Adding defensive check for uninitialized data coming from server
    let result = contents.length >= (minContents || 1);

    if (source === 'initiative') {
      result = result && initiative !== undefined && initiative.is_active;
    }

    if (source === 'topic') {
      result = result && channel !== undefined;
    }

    return result;
  };

  if (manualFetch && !loaded) {
    return <ViewTrigger offset="200px" onInview={fetchFeed} />;
  }

  if (!shouldDisplay()) {
    return null;
  }

  // Build base common props for all feed components
  const baseProps = {
    id: getSectionId(),
    title: displayTitle,
    description: displayDescription,
    seeAllText: viewAllText,
    viewAllText,
    feedUrl: viewAllUrl(),
    contents: contents,
    analyticsData: { location: `home_${source}` },
    isTopic: source === 'topic',
  };

  return (
    <section
      aria-labelledby={`${baseProps.id}--title`}
      aria-describedby={
        baseProps.description ? `${baseProps.id}--description` : undefined
      }
    >
      <div className="container">
        {displayType === 'gallery' && <ContentLayoutGallery {...baseProps} />}
        {displayType === 'hero' && <ContentLayoutHero {...baseProps} />}
        {displayType === 'grid' && (
          <ContentLayoutGrid
            {...baseProps}
            minLength={minContents || 1}
            maxLength={6}
            isFetching={isLoading}
          />
        )}
        {displayType === 'row' && (
          <ContentLayoutRow
            {...baseProps}
            minLength={minContents || 1}
            isFetching={isLoading}
          />
        )}
      </div>
    </section>
  );
};

/**
 * Deduplicates content arrays based on content IDs
 *
 * @param contentsArr - Array of content arrays to deduplicate
 * @param lengthArr - Array of max lengths for each content array
 * @returns Deduplicated array of content arrays
 */
const dedupContents = (
  contentsArr: Content[][],
  lengthArr: number[]
): Content[][] => {
  const flattenContentIds = (contents: Content[][]) =>
    Array.prototype.concat
      .apply([] as Content[], contents)
      .map((c: Content) => c.id);

  return contentsArr.reduce((arr, contents, idx) => {
    const curIds = flattenContentIds(arr);
    const uniqContents = contents
      .filter((c) => !curIds.includes(c.id))
      .slice(0, lengthArr[idx]);

    return [...arr, uniqContents];
  }, [] as Content[][]);
};

/**
 * Props for the HomeFeeds component
 */
type HomeFeedsProps = {
  contentModules: ContentHomepageModule[];
};

/**
 * HomeFeeds is the main component that displays all feed sections on the home page
 */
export const HomeFeeds = ({
  contentModules,
}: HomeFeedsProps): JSX.Element | null => {
  const numFeeds = contentModules.length;
  const [sectionsContents, setSectionsContents] = useState<Content[][]>(
    Array(numFeeds).fill([])
  );
  const [maxContents, setMaxContents] = useState<number[]>(
    Array(numFeeds).fill(0)
  );
  const uniqContents = useMemo(() => {
    return dedupContents(sectionsContents, maxContents);
  }, [sectionsContents, maxContents]);

  // Extract filter IDs for initiative tags query
  const initiativeFilterIds = useMemo(() => {
    return contentModules
      .filter(
        (mod) =>
          mod.attributes.settings.source === 'initiative' &&
          !!mod.attributes.settings.reference_ids
      )
      .flatMap((mod) => mod.attributes.settings.reference_ids as number[])
      .map((id) => String(id));
  }, [contentModules]);

  // Use React Query to fetch initiative tags
  const { data: initiativeTagsData = [] } = useInitiativeTagsQuery(
    initiativeFilterIds.length > 0 ? initiativeFilterIds : undefined
  );

  /**
   * Updates the content and max content arrays for a specific section
   *
   * @param order - The order/position of the section to update
   * @param newContents - The new content items for the section
   * @param newMaxContents - The new max content count for the section
   */
  const updateSectionContents = (
    order: number,
    newContents: Content[],
    newMaxContents: number
  ): void => {
    setMaxContents((maxContentsOld) =>
      maxContentsOld.map((length, index) => {
        return index === order ? newMaxContents : length;
      })
    );

    setSectionsContents((sectionsContentsOld) =>
      sectionsContentsOld.map((contents, index) => {
        return index === order ? newContents : contents;
      })
    );
  };

  // Update state arrays when the number of feeds changes
  useEffect(() => {
    if (numFeeds > 0) {
      setSectionsContents(Array(numFeeds).fill([]));
      setMaxContents(Array(numFeeds).fill(0));
    }
  }, [numFeeds]);

  return (
    <div id={DEFAULT_SKIP_TO_CONTENT_ID} className="home__feed-sections">
      {contentModules.map((content_module, idx) => (
        <HomeFeedSection
          order={idx}
          item={content_module}
          updateSectionContents={updateSectionContents}
          contents={uniqContents[idx] || []}
          initiativeTags={initiativeTagsData}
          key={idx}
        />
      ))}
    </div>
  );
};
